import React, { useRef, useState, useEffect } from 'react';
import { Printer, X } from 'lucide-react';
import api from '../services/api';

export interface BadgeSubject {
    id: number | string;
    first_name: string;
    last_name: string;
    phone?: string;
    card_number?: string;
    photo_url?: string | null;
    forfaitName?: string;
    endDate?: string;
    specialties?: string;
    isGroupPack?: boolean;
    groupMembers?: Array<{ full_name: string; phone?: string }>;
}

interface ClientBadgeModalProps {
    isOpen: boolean;
    onClose: () => void;
    role?: 'client' | 'coach';
    client: BadgeSubject | null;
}

const cardBase: React.CSSProperties = {
    width: 370,
    height: 230,
    background: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: 16,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    color: '#111827',
    fontFamily: 'Arial, Helvetica, sans-serif',
    overflow: 'hidden',
    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
    position: 'relative'
};

const ClientBadgeModal: React.FC<ClientBadgeModalProps> = ({ isOpen, onClose, role = 'client', client }) => {
    const printRef = useRef<HTMLDivElement>(null);
    const [fetchedMembers, setFetchedMembers] = useState<Array<{ full_name: string; phone?: string }>>([]);
    const [fetchedForfaitName, setFetchedForfaitName] = useState<string>('');

    const isCoach = role === 'coach';

    // Récupérer les membres du pack si non fournis directement
    useEffect(() => {
        if (!isOpen || !client || isCoach) return;
        if (client.groupMembers && client.groupMembers.length > 0) {
            setFetchedMembers(client.groupMembers);
            return;
        }
        api.get(`/subscriptions/client/${client.id}`)
            .then(res => {
                const list = res.data || [];
                const activeSub = list.find((s: any) => s.status === 'actif') || list[0];
                if (activeSub) {
                    if (activeSub.forfait_name || activeSub.type_name) {
                        setFetchedForfaitName(activeSub.forfait_name || activeSub.type_name);
                    }
                    if (activeSub.group_members && activeSub.group_members.length > 0) {
                        setFetchedMembers(activeSub.group_members);
                    }
                }
            })
            .catch(() => {});
    }, [isOpen, client, isCoach]);

    if (!isOpen || !client) return null;

    const effectiveMembers = (client.groupMembers && client.groupMembers.length > 0) ? client.groupMembers : fetchedMembers;
    const isGroupPack = Boolean(
        client.isGroupPack ||
        effectiveMembers.length > 0 ||
        (client.forfaitName && client.forfaitName.toLowerCase().includes('pack entreprise')) ||
        (fetchedForfaitName && fetchedForfaitName.toLowerCase().includes('pack entreprise'))
    );

    // Numéro de carte unique permanent : COACH-{id} pour coach, ou HF-MB-{id} pour client
    const cardNumber = client.card_number || (isCoach ? `COACH-${client.id}` : ('HF-MB-' + String(client.id).padStart(4, '0')));
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(cardNumber)}`;

    const handlePrint = () => {
        const w = window.open('', '_blank', 'width=800,height=600');
        if (!w || !printRef.current) return;
        w.document.write(`<html><head><title>Badge ${isCoach ? 'Coach' : 'Membre'} ${client.first_name} ${client.last_name}</title>
            <style>body{margin:20px;display:flex;gap:20px;flex-wrap:wrap;background:#fff}
            *{-webkit-print-color-adjust:exact;print-color-adjust:exact}</style></head>
            <body>${printRef.current.innerHTML}</body></html>`);
        w.document.close();
        w.focus();
        setTimeout(() => { w.print(); w.close(); }, 600);
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
            <div className="bg-[#121212] border-2 border-[#eab308] rounded-3xl max-w-3xl w-full max-h-[94vh] overflow-y-auto p-6 text-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-gray-800 pb-4 mb-6">
                    <div>
                        <span className="text-xs font-black uppercase tracking-wider text-[#eab308] block">
                            {isCoach ? 'Badge Officiel Coach' : 'Badge Membre'}
                        </span>
                        <h3 className="text-xl font-black">{client.first_name} {client.last_name}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handlePrint} className="px-4 py-2 bg-[#eab308] text-black font-extrabold text-xs rounded-xl flex items-center gap-1.5 hover:bg-yellow-400 cursor-pointer transition-colors">
                            <Printer size={16} /> Imprimer le badge
                        </button>
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-white rounded-full bg-white/10 cursor-pointer transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div ref={printRef} style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {/* RECTO - DESIGN OFFICIEL HOP FITNESS BLANC & JAUNE */}
                    <div style={{ ...cardBase, position: 'relative' }}>
                        {/* Photo Coach / Membre positionnée en haut à droite */}
                        <div style={{ position: 'absolute', top: 14, right: 16, width: 92, height: 116, borderRadius: 16, border: isCoach ? '2px solid #eab308' : '2px solid #9ca3af', overflow: 'hidden', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 8px rgba(0,0,0,0.12)', zIndex: 10 }}>
                            {client.photo_url ? (
                                <img src={client.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <div style={{ fontWeight: 900, fontSize: 26, color: '#d97706' }}>
                                    {`${client.first_name?.[0] || ''}${client.last_name?.[0] || ''}`.toUpperCase()}
                                </div>
                            )}
                        </div>

                        {/* Header : Logo + Titre (avec marge à droite pour laisser la photo respirer) */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 110px 6px 16px' }}>
                            <img 
                                src="/logohop_fitness.jpeg" 
                                alt="Logo Hop Fitness" 
                                style={{ width: 56, height: 56, objectFit: 'contain', flexShrink: 0 }} 
                            />
                            <div style={{ textAlign: 'center', flex: 1 }}>
                                <div style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', letterSpacing: 1, color: '#000', textTransform: 'uppercase', lineHeight: 1.1 }}>
                                    HOP FITNESS
                                </div>
                                <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 2, color: isCoach ? '#b45309' : isGroupPack ? '#7e22ce' : '#000', textTransform: 'uppercase', marginTop: 2 }}>
                                    {isCoach ? 'CARTE COACH' : isGroupPack ? 'CARTE PACK ENTREPRISE' : 'CARTE ADHÉRENT'}
                                </div>
                                {isGroupPack && (
                                    <div style={{ fontSize: 8, fontWeight: 900, color: '#7e22ce', textTransform: 'uppercase', marginTop: 1, letterSpacing: 0.5 }}>
                                        👥 Pack 5 personnes (1 badge)
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Body : Informations calibrées sur la gauche */}
                        <div style={{ padding: '0 16px 8px 16px', maxWidth: 245, zIndex: 2, fontSize: 11, fontWeight: 800, color: '#111827', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
                                <span style={{ width: 80, color: '#374151', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', flexShrink: 0 }}>NOM :</span>
                                <span style={{ fontSize: 12, fontWeight: 900, color: '#000', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.last_name}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
                                <span style={{ width: 80, color: '#374151', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', flexShrink: 0 }}>PRÉNOMS :</span>
                                <span style={{ fontSize: 12, fontWeight: 900, color: '#000', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.first_name}</span>
                            </div>
                            {isCoach ? (
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
                                    <span style={{ width: 80, color: '#374151', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', flexShrink: 0 }}>RÔLE :</span>
                                    <span style={{ fontSize: 11, fontWeight: 900, color: '#b45309', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {client.specialties ? `COACH • ${client.specialties}` : 'COACH SPORTIF'}
                                    </span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
                                    <span style={{ width: 80, color: '#374151', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', flexShrink: 0 }}>TÉL :</span>
                                    <span style={{ fontSize: 11, fontWeight: 900, color: '#000' }}>{client.phone || '—'}</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
                                <span style={{ width: 80, color: '#374151', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', flexShrink: 0 }}>N° CARTE :</span>
                                <span style={{ fontSize: 12, fontWeight: 900, fontFamily: 'monospace', color: '#000' }}>{cardNumber}</span>
                            </div>
                        </div>

                        {/* Footer Bandeau Jaune */}
                        <div style={{ background: '#f59e0b', color: '#000', textAlign: 'center', padding: '6px 0', fontWeight: 900, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginTop: 'auto', zIndex: 5 }}>
                            DISCIPLINE • FORCE • RESPECT • PERFORMANCE
                        </div>
                    </div>

                    {/* VERSO - DESIGN OFFICIEL HOP FITNESS BLANC & JAUNE */}
                    <div style={{ ...cardBase, alignItems: 'center', textAlign: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 10 }}>
                            <img 
                                src="/logohop_fitness.jpeg" 
                                alt="Hop Fitness Logo" 
                                style={{ width: 32, height: 32, objectFit: 'contain' }} 
                            />
                            <span style={{ fontWeight: 900, fontSize: 13, letterSpacing: 1, color: '#000', textTransform: 'uppercase' }}>
                                HOP FITNESS
                            </span>
                        </div>

                        {/* QR Code */}
                        <div style={{ background: '#fff', padding: 6, borderRadius: 14, border: '2px solid #000', boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}>
                            <img src={qrUrl} alt="QR Code" style={{ width: isGroupPack ? 64 : 88, height: isGroupPack ? 64 : 88, display: 'block' }} />
                        </div>

                        <div style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: 11, color: '#000', marginTop: -2 }}>
                            {cardNumber}
                        </div>

                        {/* Liste des membres autorisés du pack si pack groupe */}
                        {isGroupPack && effectiveMembers.length > 0 && (
                            <div style={{ background: '#faf5ff', border: '1px solid #d8b4fe', borderRadius: 8, padding: '4px 8px', width: '92%', boxSizing: 'border-box', textAlign: 'left', margin: '2px auto' }}>
                                <div style={{ fontWeight: 900, color: '#7e22ce', textTransform: 'uppercase', fontSize: 8, letterSpacing: 0.5, marginBottom: 2 }}>
                                    👥 Bénéficiaires du Pack (Max 5 pers. / 1 badge) :
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: effectiveMembers.length > 2 ? '1fr 1fr' : '1fr', gap: '1px 6px', color: '#111827', fontSize: 8.5, fontWeight: 800 }}>
                                    {effectiveMembers.slice(0, 5).map((m, idx) => (
                                        <div key={idx} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {idx + 1}. {m.full_name || (m as any).name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Footer Bandeau Jaune */}
                        <div style={{ background: '#f59e0b', color: '#000', width: '100%', padding: '6px 0', fontWeight: 900, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', marginTop: 'auto' }}>
                            {isCoach ? 'STAFF TECHNIQUE • SCANNEZ À L\'ACCUEIL' : isGroupPack ? 'PACK ENTREPRISE • 1 SEUL BADGE POUR LE GROUPE' : 'DÉPASSE TES LIMITES • SCANNEZ À L\'ACCUEIL'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientBadgeModal;
